import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import watchVideoService from '../../services/watchVideoService';

export const getWatchVideos = createAsyncThunk(
  'watchVideos/getWatchVideos',
  async (params, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.getWatchVideos(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get watch videos');
    }
  }
);

export const getWatchVideo = createAsyncThunk(
  'watchVideos/getWatchVideo',
  async (id, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.getWatchVideo(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get watch video');
    }
  }
);

export const createWatchVideo = createAsyncThunk(
  'watchVideos/createWatchVideo',
  async (watchVideoData, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.createWatchVideo(watchVideoData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create watch video');
    }
  }
);

export const updateWatchVideo = createAsyncThunk(
  'watchVideos/updateWatchVideo',
  async ({ id, watchVideoData }, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.updateWatchVideo(id, watchVideoData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update watch video');
    }
  }
);

export const deleteWatchVideo = createAsyncThunk(
  'watchVideos/deleteWatchVideo',
  async (id, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.deleteWatchVideo(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete watch video');
    }

  }
);

export const getVideoRequests = createAsyncThunk(
  'watchVideos/getVideoRequests',
  async (status, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.getVideoRequests(status);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to get video requests');
    }
  }
);

export const approveVideoRequest = createAsyncThunk(
  'watchVideos/approveVideoRequest',
  async (id, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.approveVideoRequest(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to approve video request');
    }
  }
);

export const declineVideoRequest = createAsyncThunk(
  'watchVideos/declineVideoRequest',
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await watchVideoService.declineVideoRequest({ id, reason });
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to decline video request');
    }
  }
);

const initialState = {
  watchVideos: [],
  videoRequests: [],
  currentWatchVideo: null,
  isLoading: false,
  error: null,
  message: null,
};

const watchVideoSlice = createSlice({
  name: 'watchVideos',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.message = null;
    },
    clearCurrentWatchVideo: (state) => {
      state.currentWatchVideo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get watch videos
      .addCase(getWatchVideos.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWatchVideos.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle both array response and object with watchVideos property
        state.watchVideos = Array.isArray(action.payload)
          ? action.payload
          : (action.payload.watchVideos || action.payload || []);
        state.error = null;
      })
      .addCase(getWatchVideos.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get single watch video
      .addCase(getWatchVideo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getWatchVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentWatchVideo = action.payload;
        state.error = null;
      })
      .addCase(getWatchVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Create watch video
      .addCase(createWatchVideo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createWatchVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.watchVideos.unshift(action.payload);
        state.error = null;
        state.message = 'Watch video created successfully';
      })
      .addCase(createWatchVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update watch video
      .addCase(updateWatchVideo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateWatchVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.watchVideos.findIndex(v => v._id === action.payload._id);
        if (index !== -1) {
          state.watchVideos[index] = action.payload;
        }
        state.currentWatchVideo = action.payload;
        state.error = null;
        state.message = 'Watch video updated successfully';
      })
      .addCase(updateWatchVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete watch video
      .addCase(deleteWatchVideo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWatchVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.watchVideos = state.watchVideos.filter(v => v._id !== action.meta.arg);
        state.error = null;
        state.message = 'Watch video deleted successfully';
      })
      .addCase(deleteWatchVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get video requests
      .addCase(getVideoRequests.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getVideoRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoRequests = action.payload;
        state.error = null;
      })
      .addCase(getVideoRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Approve video request
      .addCase(approveVideoRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(approveVideoRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoRequests = state.videoRequests.filter(req => req._id !== action.meta.arg);
        // Optionally add to watchVideos if we return the new watchVideo object
        if (action.payload.watchVideo) {
          state.watchVideos.unshift(action.payload.watchVideo);
        }
        state.error = null;
        state.message = 'Video request approved successfully';
      })
      .addCase(approveVideoRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Decline video request
      .addCase(declineVideoRequest.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(declineVideoRequest.fulfilled, (state, action) => {
        state.isLoading = false;
        state.videoRequests = state.videoRequests.filter(req => req._id !== action.meta.arg.id);
        state.error = null;
        state.message = 'Video request declined successfully';
      })
      .addCase(declineVideoRequest.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentWatchVideo } = watchVideoSlice.actions;
export default watchVideoSlice.reducer;



